import React from "react";
import { Helmet } from "react-helmet-async";
import limassolImage from "../assets/cyprus/limassol-marina.webp";
import { trackPhoneClick, trackWhatsAppClick } from "../utils/analytics";

export default function ThingsToDoLimassolPage() {
  const phoneNumber = "+35796613191";
  const whatsappLink =
    "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

  const attractions = [
    {
      name: "Limassol Marina",
      description:
        "A modern waterfront area with restaurants, yachts, sea views, shopping, and an easy walk into the old town.",
      image: limassolImage,
    },
    {
      name: "Limassol Old Town",
      description:
        "The historic heart of the city, full of narrow streets, cafes, bars, boutiques, and lively evening atmosphere.",
      image:
        "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Limassol Castle",
      description:
        "A central landmark close to the old market and marina, ideal for visitors who want culture, history, and a compact city walk.",
      image:
        "https://images.unsplash.com/photo-1533154683836-84ea7a0bc310?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Fasouri Watermania",
      description:
        "A popular family day out near Limassol with slides, pools, shaded areas, and simple taxi access from hotels and apartments.",
      image:
        "https://images.unsplash.com/photo-1562859879-bd0c3c2f0a56?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  const beaches = [
    {
      name: "Lady's Mile Beach",
      description:
        "One of the easiest beaches to reach from Limassol, known for shallow water, long sandy stretches, and seaside restaurants.",
      image:
        "https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Governor's Beach",
      description:
        "A scenic beach area east of Limassol with white rocks, dark sand, fish tavernas, and calm Mediterranean views.",
      image:
        "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Limassol Seafront",
      description:
        "A relaxed coastal promenade for walking, coffee, cycling, sunset views, and quick city rides between hotels and restaurants.",
      image:
        "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  const historicSites = [
    {
      name: "Kourion Archaeological Site",
      description:
        "One of the best attractions near Limassol, with an ancient theatre, mosaics, ruins, and dramatic sea views.",
      image:
        "https://images.unsplash.com/photo-1603565816030-6b389eeb23cb?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Kolossi Castle",
      description:
        "A medieval castle west of Limassol, closely linked with Commandaria wine heritage and easy to combine with Kourion.",
      image:
        "https://images.unsplash.com/photo-1533154683836-84ea7a0bc310?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Limassol Castle",
      description:
        "A convenient city-centre stop for travellers exploring Limassol Old Town, the market area, and nearby dining streets.",
      image:
        "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  const dayTrips = [
    {
      name: "Omodos Village",
      description:
        "A traditional wine village with stone lanes, tavernas, local shops, churches, and a classic mountain-village feel.",
      image:
        "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Troodos Mountains",
      description:
        "A cool mountain escape for viewpoints, wineries, monasteries, forest roads, and private sightseeing taxi tours.",
      image:
        "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    },
    {
      name: "Kourion and Kolossi",
      description:
        "A classic Limassol taxi tour pairing ancient history, medieval architecture, coastal views, and flexible pickup times.",
      image:
        "https://images.unsplash.com/photo-1605101100278-5d1deb2b6498?auto=format&fit=crop&w=1200&q=80",
    },
  ];

  const transportOptions = [
    "Private taxi transfers from hotels, apartments, Limassol Marina, Old Town, and nearby resorts",
    "Flexible taxi tours for beaches, villages, wineries, castles, and archaeological sites",
    "Airport transfers between Limassol, Larnaca Airport, and Paphos Airport",
    "Door-to-door service for families, couples, cruise visitors, and business travellers",
  ];

  const faqs = [
    {
      question: "What are the best things to do in Limassol?",
      answer:
        "The best things to do in Limassol include visiting Limassol Marina, Limassol Old Town, Limassol Castle, Lady's Mile Beach, Governor's Beach, Kourion Archaeological Site, Kolossi Castle, Fasouri Watermania, Omodos Village, and the Troodos Mountains.",
    },
    {
      question: "Can I book Limassol taxi tours for sightseeing?",
      answer:
        "Yes. Private Limassol taxi tours are available for beaches, historic sites, wine villages, Troodos Mountains, airport transfers, and custom day trips from your hotel or apartment.",
    },
    {
      question: "What beaches are near Limassol?",
      answer:
        "Popular beaches near Limassol include Lady's Mile Beach, Governor's Beach, the city seafront beaches, and beach areas along the coastal road east and west of the city.",
    },
    {
      question: "What historic sites are close to Limassol?",
      answer:
        "Kourion Archaeological Site, Kolossi Castle, and Limassol Castle are among the easiest historic sites to visit from Limassol by private taxi.",
    },
    {
      question: "What are the best day trips from Limassol?",
      answer:
        "Popular day trips from Limassol include Omodos Village, Troodos Mountains, Kourion, Kolossi Castle, Governor's Beach, wineries, and traditional mountain villages.",
    },
  ];

  const renderImageCard = (item) => (
    <article
      key={item.name}
      className="group overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/50"
    >
      <div className="relative h-60 overflow-hidden">
        <img
          src={item.image}
          alt={item.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="text-2xl font-bold text-yellow-400">{item.name}</h3>
        </div>
      </div>

      <div className="p-6">
        <p className="leading-7 text-neutral-400">{item.description}</p>

        <a
          href={whatsappLink}
          onClick={() => trackWhatsAppClick("things_to_do_limassol_attraction_card")}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-block rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-neutral-950 transition-colors duration-200 hover:bg-yellow-300"
        >
          Book Taxi Tour
        </a>
      </div>
    </article>
  );

  return (
    <main className="min-h-screen bg-neutral-950 text-white">
      <Helmet>
        <title>Things to Do in Limassol | Limassol Travel Guide</title>
        <meta
          name="description"
          content="Discover the best things to do in Limassol including Limassol Marina, Old Town, beaches, Kourion, wine villages and day trips. Book private taxi transfers in Limassol."
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Things to Do in Limassol | Limassol Travel Guide"
        />
        <meta
          property="og:description"
          content="Discover the best things to do in Limassol including Limassol Marina, Old Town, beaches, Kourion, wine villages and day trips. Book private taxi transfers in Limassol."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://limassolairporttaxi.com/things-to-do-in-limassol"
        />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Things to Do in Limassol | Limassol Travel Guide"
        />
        <meta
          name="twitter:description"
          content="Discover the best things to do in Limassol including Limassol Marina, Old Town, beaches, Kourion, wine villages and day trips. Book private taxi transfers in Limassol."
        />
        <meta
          name="twitter:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <link
          rel="canonical"
          href="https://limassolairporttaxi.com/things-to-do-in-limassol"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <section
        className="relative overflow-hidden bg-neutral-900 px-6 pb-28 pt-40 md:pb-36 md:pt-48"
      >
        <img
          src={limassolImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/35 to-black/50" />

        <div className="relative mx-auto max-w-7xl">
          <div className="max-w-4xl">
            <p className="font-semibold text-yellow-300">
              Limassol Travel Guide
            </p>

            <h1 className="mt-4 text-5xl font-bold text-yellow-400 md:text-7xl">
              Things to Do in Limassol
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-neutral-200">
              Explore the best attractions in Limassol, from Limassol Marina
              and Old Town to beaches, castles, Kourion, wine villages, and
              private Limassol taxi tours.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href={`tel:${phoneNumber}`}
                onClick={() => trackPhoneClick("things_to_do_limassol_hero")}
                className="rounded-2xl bg-yellow-400 px-8 py-4 text-center font-bold text-neutral-950 transition-colors duration-200 hover:bg-yellow-300"
              >
                Call Now
              </a>

              <a
                href={whatsappLink}
                onClick={() => trackWhatsAppClick("things_to_do_limassol_hero")}
                target="_blank"
                rel="noreferrer"
                className="rounded-2xl border border-white/10 bg-white/[0.04] px-8 py-4 text-center font-bold text-white transition-colors duration-200 hover:border-yellow-400/50 hover:text-yellow-300"
              >
                WhatsApp Booking
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-3xl">
          <p className="font-semibold text-yellow-300">
            Best Attractions in Limassol
          </p>

          <h2 className="mt-3 text-4xl font-bold text-yellow-400">
            Best Things to Do in Limassol
          </h2>

          <p className="mt-4 leading-7 text-neutral-400">
            Limassol blends city life, seafront dining, culture, family
            attractions, and quick access to some of the most rewarding places
            to visit in Cyprus.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {attractions.map(renderImageCard)}
        </div>
      </section>

      <section className="bg-neutral-900 px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="font-semibold text-yellow-300">Beach Days</p>

            <h2 className="mt-3 text-4xl font-bold text-yellow-400">
              Beaches Near Limassol
            </h2>

            <p className="mt-4 leading-7 text-neutral-400">
              Plan a relaxed day by the sea with private taxi pickup from your
              hotel, apartment, Limassol Marina, or Old Town.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {beaches.map(renderImageCard)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-3xl">
          <p className="font-semibold text-yellow-300">History and Culture</p>

          <h2 className="mt-3 text-4xl font-bold text-yellow-400">
            Historic Sites Near Limassol
          </h2>

          <p className="mt-4 leading-7 text-neutral-400">
            Add culture to your Limassol travel guide with castles, ancient
            ruins, sea-view archaeology, and easy door-to-door transfers.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {historicSites.map(renderImageCard)}
        </div>
      </section>

      <section className="bg-neutral-900 px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="font-semibold text-yellow-300">
              Limassol Taxi Tours
            </p>

            <h2 className="mt-3 text-4xl font-bold text-yellow-400">
              Day Trips from Limassol
            </h2>

            <p className="mt-4 leading-7 text-neutral-400">
              Limassol is a practical base for wine villages, Troodos
              Mountains, archaeological sites, beaches, and custom sightseeing
              routes across Cyprus.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {dayTrips.map(renderImageCard)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:grid-cols-[0.85fr_1.15fr] md:p-10">
          <div>
            <p className="font-semibold text-yellow-300">Getting Around</p>

            <h2 className="mt-3 text-4xl font-bold text-yellow-400">
              How to Get Around Limassol
            </h2>

            <p className="mt-5 leading-8 text-neutral-300">
              A private taxi is the easiest way to connect Limassol hotels,
              beaches, Old Town, the marina, cruise areas, wineries, historic
              sites, and airport transfers without waiting or changing buses.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href={`tel:${phoneNumber}`}
                onClick={() => trackPhoneClick("things_to_do_limassol_mid_cta")}
                className="rounded-2xl bg-yellow-400 px-7 py-4 text-center font-bold text-neutral-950 transition-colors duration-200 hover:bg-yellow-300"
              >
                Call Now
              </a>

              <a
                href={whatsappLink}
                onClick={() => trackWhatsAppClick("things_to_do_limassol_mid_cta")}
                target="_blank"
                rel="noreferrer"
                className="rounded-2xl border border-white/10 bg-neutral-950 px-7 py-4 text-center font-bold text-white transition-colors duration-200 hover:border-yellow-400/50 hover:text-yellow-300"
              >
                WhatsApp Booking
              </a>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {transportOptions.map((option) => (
              <div
                key={option}
                className="rounded-3xl border border-white/10 bg-black/30 p-6"
              >
                <div className="mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-yellow-400/10 text-yellow-400">
                  ✓
                </div>
                <p className="font-semibold leading-7 text-neutral-200">
                  {option}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-neutral-900 px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <p className="font-semibold text-yellow-300">Travel Questions</p>

            <h2 className="mt-3 text-4xl font-bold text-yellow-400">FAQ</h2>
          </div>

          <div className="space-y-5">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-6"
              >
                <h3 className="text-xl font-bold text-yellow-400">
                  {faq.question}
                </h3>

                <p className="mt-3 leading-7 text-neutral-400">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04]">
          <div className="grid md:grid-cols-2">
            <div className="relative min-h-[360px]">
              <img
                src={limassolImage}
                alt="Limassol taxi tours and private transfers"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            </div>

            <div className="p-8 md:p-12">
              <p className="font-semibold text-yellow-300">
                Private Taxi Transfers in Limassol
              </p>

              <h2 className="mt-3 text-4xl font-bold text-yellow-400">
                Book Your Limassol Taxi Tour
              </h2>

              <p className="mt-5 text-lg leading-8 text-neutral-300">
                Travel comfortably to Limassol Marina, Old Town, beaches,
                Kourion, Kolossi Castle, Omodos Village, Troodos Mountains, and
                airports across Cyprus.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={`tel:${phoneNumber}`}
                  onClick={() => trackPhoneClick("things_to_do_limassol_final_cta")}
                  className="rounded-2xl bg-yellow-400 px-8 py-4 text-center font-bold text-neutral-950 transition-colors duration-200 hover:bg-yellow-300"
                >
                  Call Now
                </a>

                <a
                  href={whatsappLink}
                  onClick={() => trackWhatsAppClick("things_to_do_limassol_final_cta")}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl border border-white/10 bg-neutral-950 px-8 py-4 text-center font-bold text-white transition-colors duration-200 hover:border-yellow-400/50 hover:text-yellow-300"
                >
                  WhatsApp Booking
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
